(() => {
  "use strict";

  const LOAD_WHEN = {
    ON_LOAD: "OnLoad",
    FIRST_INTERACTION: "FirstInteraction",
    DOM_CONTENT_LOADED: "DomContentLoaded",
    IMMEDIATELY: "Immediately",
    NEVER: "Never",
  };

  const MATCH_WHEN = {
    DOM: "DOM",
    DYNAMIC_INSERT: "DynamicInsert",
  };

  const APPENDED_MARKER = "lazyscript-appended";
  const INTERACTION_EVENTS = ["mousemove", "scroll", "keydown", "click", "touchstart"];

  function executeScriptSafely(source) {
    try {
      eval(source);
    } catch (error) {
      console.error("Failed to execute delayed script", { error, source });
    }
  }

  function hasString(value) {
    return typeof value === "string" && value.length > 0;
  }

  function isAlreadyManaged(element, marker) {
    return marker.some((entry) => entry.originalElement === element);
  }

  class DelayedAssetRule {
    constructor(isDisabled) {
      this.isDisabled = Boolean(isDisabled);
      this.loadType = LOAD_WHEN.ON_LOAD;
      this.matchType = MATCH_WHEN.DOM;
      this.matchOptions = {};
      this.matches = [];
    }

    loadOn(loadType) {
      this.loadType = loadType;
      return this;
    }

    matchOn(matchType) {
      this.matchType = matchType;
      return this;
    }

    isScriptMatch(element) {
      return this._isMatch(element, {
        src: element.src,
        content: element.textContent,
        type: element.type,
      });
    }

    isLinkMatch(element) {
      return this._isMatch(element, {
        href: element.href,
        rel: element.rel,
      });
    }

    isIframeMatch(element) {
      return this._isMatch(element, {
        src: element.src,
      });
    }

    isImageMatch(element) {
      return this._isMatch(element, {
        src: element.src,
        dataSrc: element.dataset?.src,
      });
    }

    _isMatch(element, values) {
      const options = this.matchOptions;
      const matched = (
        (hasString(values.src) && hasString(options.src) && values.src.includes(options.src)) ||
        (hasString(values.dataSrc) && hasString(options.src) && values.dataSrc.includes(options.src)) ||
        (hasString(values.content) && hasString(options.contentPart) && values.content.includes(options.contentPart)) ||
        (hasString(values.type) && hasString(options.type) && values.type === options.type) ||
        (hasString(values.href) && hasString(options.href) && values.href.includes(options.href)) ||
        (hasString(values.rel) && hasString(options.rel) && values.rel === options.rel) ||
        (hasString(options.queryMatch) && element.matches(options.queryMatch))
      );

      return matched && !isAlreadyManaged(element, this.matches);
    }

    registerMatch(element, prepareMatch) {
      const match = {
        parent: element.parentElement || document.head,
        originalElement: element,
        isAppended: false,
      };

      if (typeof prepareMatch === "function") {
        prepareMatch(element, match);
      }

      if (this.loadType === LOAD_WHEN.NEVER) {
        return;
      }

      this.matches.push(match);

      const shouldLoadNow = this.loadType === LOAD_WHEN.IMMEDIATELY || this.isDisabled;
      if (!shouldLoadNow) {
        return;
      }

      const waitsForBody = (
        !document.body &&
        element instanceof HTMLScriptElement &&
        hasString(element.innerHTML) &&
        element.innerHTML.includes("document.body")
      );

      if (waitsForBody) {
        document.addEventListener("DOMContentLoaded", () => this.load(), { once: true });
        return;
      }

      this.load();
    }

    appendPreparedNode(match, node) {
      node.dataset.lazyscriptAppended = APPENDED_MARKER;

      if (this.loadType === LOAD_WHEN.DOM_CONTENT_LOADED && document.body) {
        document.body.appendChild(node);
        return;
      }

      match.parent.appendChild(node);
    }
  }

  class ScriptDelayRule extends DelayedAssetRule {
    constructor(matchOptions, isDisabled) {
      super(isDisabled);
      this.matchOptions = matchOptions;
    }

    tryMatch(node) {
      if (!(node instanceof HTMLScriptElement)) {
        return false;
      }

      if (!this.isScriptMatch(node)) {
        return false;
      }

      const payload = node.src || node.textContent || "";
      node.type = "delayed-script";

      this.registerMatch(node, (element, match) => {
        match.payload = payload;
        match.mode = element.src ? "src" : "inline";
      });

      return true;
    }

    load() {
      for (const match of this.matches.filter((item) => !item.isAppended)) {
        match.isAppended = true;

        const script = document.createElement("script");
        script.className = match.originalElement.className;

        if (match.mode === "src") {
          script.src = match.payload;
        } else {
          script.innerHTML = match.originalElement.innerHTML;
        }

        this.appendPreparedNode(match, script);
      }
    }
  }

  class LinkDelayRule extends DelayedAssetRule {
    constructor(matchOptions, isDisabled) {
      super(isDisabled);
      this.matchOptions = matchOptions;
    }

    tryMatch(node) {
      if (!(node instanceof HTMLLinkElement)) {
        return false;
      }

      if (!this.isLinkMatch(node)) {
        return false;
      }

      node.rel = "delayed-css";
      this.registerMatch(node);
      return true;
    }

    load() {
      for (const match of this.matches.filter((item) => !item.isAppended)) {
        match.isAppended = true;
        const link = document.createElement("link");
        link.href = match.originalElement.href;
        link.rel = "stylesheet";
        this.appendPreparedNode(match, link);
      }
    }
  }

  class IframeDelayRule extends DelayedAssetRule {
    constructor(matchOptions, isDisabled) {
      super(isDisabled);
      this.matchOptions = matchOptions;
    }

    tryMatch(node) {
      if (!(node instanceof HTMLIFrameElement)) {
        return false;
      }

      if (!this.isIframeMatch(node)) {
        return false;
      }

      node.setAttribute("data-lazyscript-src", node.getAttribute("src") || "");
      node.removeAttribute("src");
      this.registerMatch(node);
      return true;
    }

    load() {
      for (const match of this.matches.filter((item) => !item.isAppended)) {
        match.originalElement.setAttribute(
          "src",
          match.originalElement.getAttribute("data-lazyscript-src") || "",
        );
        match.isAppended = true;
      }
    }
  }

  class ImageDelayRule extends DelayedAssetRule {
    constructor(matchOptions, isDisabled) {
      super(isDisabled);
      this.matchOptions = matchOptions;
    }

    tryMatch(node) {
      if (!(node instanceof HTMLImageElement)) {
        return false;
      }

      if (!this.isImageMatch(node)) {
        return false;
      }

      this.registerMatch(node, (image) => {
        image.dataset.src = image.dataset.src || image.src;
        image.dataset.srcset = image.dataset.srcset || image.srcset;
        image.removeAttribute("src");
        image.removeAttribute("srcset");
      });

      return true;
    }

    load() {
      for (const match of this.matches.filter((item) => !item.isAppended)) {
        match.isAppended = true;
        match.originalElement.setAttribute("src", match.originalElement.dataset.src || "");
        match.originalElement.setAttribute("srcset", match.originalElement.dataset.srcset || "");
      }
    }
  }

  class SequencedScriptDelayRule extends DelayedAssetRule {
    constructor(typePattern, isDisabled) {
      super(isDisabled);
      this.typePattern = typePattern;
    }

    tryMatch(node) {
      if (!(node instanceof HTMLScriptElement)) {
        return false;
      }

      const prefix = this.typePattern.replace("*", "");
      if (!node.type.includes(prefix)) {
        return false;
      }

      const orderValue = node.type.replace(prefix, "");
      const order = Number(orderValue);
      const payload = node.src || node.textContent || "";

      if (!orderValue || Number.isNaN(order) || this.matches.some((entry) => entry.payload === payload)) {
        return false;
      }

      node.type = "delayed-script";
      this.registerMatch(node, (element, match) => {
        match.payload = payload;
        match.mode = element.src ? "src" : "inline";
        match.order = order;
      });

      return true;
    }

    load() {
      const firstOrder = Math.min(...this.matches.map((entry) => entry.order), 0);
      this.loadInOrder(firstOrder);
    }

    loadInOrder(order) {
      const currentBatch = this.matches.filter((entry) => entry.order === order);
      const previousBatchPending = this.matches
        .filter((entry) => entry.order === order - 1)
        .some((entry) => !entry.isAppended);
      const allDone = this.matches.every((entry) => entry.isAppended);

      if (previousBatchPending || allDone) {
        return;
      }

      for (const match of currentBatch) {
        if (match.mode === "src") {
          const script = document.createElement("script");
          script.src = match.payload;

          const continueSequence = () => {
            match.isAppended = true;
            this.loadInOrder(order + 1);
          };

          script.onload = continueSequence;
          script.onerror = continueSequence;
          this.appendPreparedNode(match, script);
          continue;
        }

        const script = document.createElement("script");
        script.innerHTML = match.originalElement.innerHTML;
        this.appendPreparedNode(match, script);
        match.isAppended = true;
      }

      this.loadInOrder(order + 1);
    }
  }

  class LazyScriptOptimizer {
    constructor(isDisabled) {
      this.isDisabled = Boolean(isDisabled);
      this.rules = [];
      this.domRules = null;
      this.dynamicRules = null;
      this.eventHandlers = {};
      this.interceptedListeners = [];
      this.appendedStyles = [];
      this.loadEventData = undefined;
      this.domContentLoadedEventData = undefined;

      this.observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== "childList") {
            continue;
          }

          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLBodyElement) {
              this.overrideBodyAppend(node);
              continue;
            }

            if (!(node instanceof HTMLElement)) {
              continue;
            }

            if (node.dataset?.lazyscriptAppended === APPENDED_MARKER) {
              continue;
            }

            for (const rule of this.getDomRules()) {
              if (rule.tryMatch(node)) {
                break;
              }
            }
          }
        }
      });
    }

    observeDocument() {
      this.observer.observe(document, { childList: true, subtree: true });
      this.overrideHeadAppend(document.head);

      document.addEventListener("DOMContentLoaded", this.onDomContentLoaded.bind(this));

      if (document.readyState === "complete") {
        this.onLoad();
      } else {
        window.addEventListener("load", this.onLoad.bind(this));
      }
    }

    delayScript(matchOptions) {
      return this.addRule(new ScriptDelayRule(matchOptions, this.isDisabled));
    }

    delayLink(matchOptions) {
      return this.addRule(new LinkDelayRule(matchOptions, this.isDisabled));
    }

    delayIframe(matchOptions) {
      return this.addRule(new IframeDelayRule(matchOptions, this.isDisabled));
    }

    optimizeImage(matchOptions) {
      return this.addRule(new ImageDelayRule(matchOptions, this.isDisabled));
    }

    lazyLoadSequencedScripts(typePattern) {
      return this.addRule(new SequencedScriptDelayRule(typePattern, this.isDisabled));
    }

    addRule(rule) {
      this.rules.push(rule);
      return rule;
    }

    appendCss(cssText, removeOn) {
      if (this.isDisabled) {
        return;
      }

      const style = document.createElement("style");
      style.type = "text/css";
      style.appendChild(document.createTextNode(cssText));
      document.head.appendChild(style);

      this.appendedStyles.push({ element: style, removeOn });
    }

    appendHtml(html) {
      if (!this.isDisabled) {
        document.write(html);
      }
    }

    getCookie(name) {
      const cookie = document.cookie
        .split(";")
        .map((value) => value.trim())
        .find((value) => value.startsWith(`${name}=`));

      return cookie ? cookie.slice(name.length + 1) : null;
    }

    getCustomerId() {
      return this.getCookie("_shopify_y");
    }

    getCartToken() {
      return this.getCookie("cart");
    }

    getShop() {
      return window?.Shopify?.shop;
    }

    shouldBeOff() {
      return Math.random() < 0.05;
    }

    getDomRules() {
      if (!this.domRules) {
        this.domRules = this.rules.filter((rule) => rule.matchType === MATCH_WHEN.DOM);
      }

      return this.domRules;
    }

    getDynamicRules() {
      if (!this.dynamicRules) {
        this.dynamicRules = this.rules.filter((rule) => rule.matchType === MATCH_WHEN.DYNAMIC_INSERT);
      }

      return this.dynamicRules;
    }

    interceptLoadListeners() {
      const nativeAddEventListener = document.addEventListener;
      const captureDeferredListener = (eventName, handler) => {
        const eventData = eventName === "load" ? this.loadEventData : this.domContentLoadedEventData;
        this.interceptedListeners.push({
          id: Math.random().toString(16).slice(2),
          run: () => handler.call(this, eventData),
        });
      };

      const patchedAddEventListener = function (eventName, handler, options) {
        if (eventName === "load" || eventName === "DOMContentLoaded") {
          captureDeferredListener(eventName, handler);
          return;
        }

        return nativeAddEventListener.call(this, eventName, handler, options);
      };

      document.addEventListener = patchedAddEventListener;
      window.addEventListener = patchedAddEventListener;
    }

    flushInterceptedListeners() {
      const pendingIds = [];

      this.interceptedListeners.forEach((listener) => {
        listener.run();
        pendingIds.push(listener.id);
      });

      for (const id of pendingIds) {
        const index = this.interceptedListeners.findIndex((listener) => listener.id === id);
        if (index >= 0) {
          this.interceptedListeners.splice(index, 1);
        }
      }
    }

    overrideBodyAppend(body) {
      const nativeAppendChild = body.appendChild;
      const dynamicRules = this.getDynamicRules();

      body.appendChild = (node) => {
        if (node instanceof HTMLElement && node.dataset?.lazyscriptAppended !== APPENDED_MARKER) {
          for (const rule of dynamicRules) {
            if (rule.tryMatch(node)) {
              break;
            }
          }
        }

        return nativeAppendChild.call(body, node);
      };
    }

    overrideHeadAppend(head) {
      const nativeAppendChild = head.appendChild;
      head.appendChild = (node) => {
        if (node instanceof HTMLElement && node.dataset?.lazyscriptAppended !== APPENDED_MARKER) {
          for (const rule of this.getDynamicRules()) {
            rule.tryMatch(node);
          }
        }

        return nativeAppendChild.call(head, node);
      };

      const nativeInsertBefore = head.insertBefore;
      head.insertBefore = (node, referenceNode) => {
        if (node instanceof HTMLElement && node.dataset?.lazyscriptAppended !== APPENDED_MARKER) {
          for (const rule of this.getDynamicRules()) {
            rule.tryMatch(node);
          }
        }

        return nativeInsertBefore.call(head, node, referenceNode);
      };
    }

    onDomContentLoaded(event) {
      this.domContentLoadedEventData = event;
      this.observer.disconnect();

      for (const rule of this.rules.filter((entry) => entry.loadType === LOAD_WHEN.DOM_CONTENT_LOADED)) {
        rule.load();
      }

      this.appendedStyles = this.appendedStyles.filter((entry) => {
        if (entry.removeOn !== LOAD_WHEN.DOM_CONTENT_LOADED) {
          return true;
        }

        entry.element.remove();
        return false;
      });
    }

    onLoad(event) {
      this.loadEventData = event;
      this.interceptLoadListeners();

      for (const rule of this.rules.filter((entry) => entry.loadType === LOAD_WHEN.ON_LOAD)) {
        rule.load();
      }

      setTimeout(() => {
        this.flushInterceptedListeners();
        requestAnimationFrame(() => this.attachInteractionListeners());
      }, 10);
    }

    onFirstInteraction() {
      for (const rule of this.rules.filter((entry) => entry.loadType === LOAD_WHEN.FIRST_INTERACTION)) {
        rule.load();
        this.flushInterceptedListeners();
      }

      setTimeout(() => this.removeInteractionListeners(), 5000);
    }

    attachInteractionListeners() {
      const handler = this.onFirstInteraction.bind(this);

      for (const eventName of INTERACTION_EVENTS) {
        this.eventHandlers[eventName] = handler;
        document.addEventListener(eventName, handler);
      }
    }

    removeInteractionListeners() {
      for (const [eventName, handler] of Object.entries(this.eventHandlers)) {
        document.removeEventListener(eventName, handler);
      }
    }
  }

  function bootLazyOptimizer() {
    if (typeof MutationObserver === "undefined" || MutationObserver === null) {
      return;
    }

    const isDisabled = location?.search?.includes("lazyscript=disable");
    const optimizer = new LazyScriptOptimizer(isDisabled);

    optimizer.observeDocument();

    optimizer
      .delayScript({ queryMatch: "#web-pixels-manager-setup" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ type: "lazyload-script" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.ON_LOAD);

    optimizer
      .delayScript({ type: "lazyload-script-fi" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ type: "lazyload-script-dcl" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.DOM_CONTENT_LOADED);

    optimizer
      .delayLink({ rel: "lazyload-script-fi" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ src: "klaviyo.js?company_id=WYnxBK" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ src: "window._klOnsite" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ queryMatch: ".analytics" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ queryMatch: ".boomerang" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ src: "assets/storefront/features" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ src: "assets/shopify_pay" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ src: "connect.facebook.net" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ contentPart: "asyncLoad()" })
      .matchOn(MATCH_WHEN.DOM)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ contentPart: "instant.min.js" })
      .matchOn(MATCH_WHEN.DYNAMIC_INSERT)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ contentPart: "fbevents.js" })
      .matchOn(MATCH_WHEN.DYNAMIC_INSERT)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);

    optimizer
      .delayScript({ src: "analytics.tiktok" })
      .matchOn(MATCH_WHEN.DYNAMIC_INSERT)
      .loadOn(LOAD_WHEN.FIRST_INTERACTION);
  }

  bootLazyOptimizer();
})();
